
import React, { useState, useEffect } from 'react';
import { AppNode, WorkflowNodeData, ProcessDefinition, Edge } from '../../types';
import * as LucideIcons from 'lucide-react';
import { Settings2, Bug, Maximize2, PanelRightClose, PanelRightOpen, ArrowRight, GitFork, AlertTriangle, CheckCircle } from 'lucide-react';
import { PropertyField } from '../molecules/PropertyField';
import { useEditorConfig } from '../../contexts/EditorContext';
import { TabConfig } from '../../utils/propertyPanelRegistry';
import { VariableDefinition } from '../../utils/variableExtraction';

interface PropertiesPanelProps {
  selectedNode: AppNode | null;
  selectedEdge: Edge | null;
  activeProcess: ProcessDefinition | undefined;
  nodes: AppNode[]; // Needed for source lookup in Edge mode
  edges: Edge[]; // Needed for validation
  updateNodeData: (id: string, data: Partial<WorkflowNodeData>) => void;
  updateProcessData: (id: string, data: Partial<ProcessDefinition>) => void;
  updateEdgeData: (id: string, data: Partial<Edge>) => void;
  onRenameNode: (oldId: string, newId: string) => void;
  onOpenModal: () => void;
  variableContext?: VariableDefinition[];
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ 
  selectedNode, 
  selectedEdge,
  activeProcess,
  nodes,
  edges,
  updateNodeData, 
  updateProcessData,
  updateEdgeData,
  onRenameNode,
  onOpenModal,
  variableContext = []
}) => {
  const { nodeTypes, propertyRegistry, componentRegistry } = useEditorConfig();
  const [localData, setLocalData] = useState<any>({});
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [nodeIdInput, setNodeIdInput] = useState('');
  const [idError, setIdError] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsCollapsed(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedNode) {
      setLocalData(selectedNode.data);
      setNodeIdInput(selectedNode.id);
      setIdError(false);
      if (window.innerWidth >= 768) setIsCollapsed(false);
    } else if (selectedEdge) {
      // For edges, we primarily manage the label/path
      setLocalData({ label: selectedEdge.label || '' });
      if (window.innerWidth >= 768) setIsCollapsed(false);
    } else if (activeProcess) {
      setLocalData({
        name: activeProcess.name,
        description: activeProcess.description || '',
        id: activeProcess.id
      });
    }
  }, [selectedNode, selectedEdge, activeProcess]);

  const handleIdBlur = () => {
      if (selectedNode && nodeIdInput && nodeIdInput !== selectedNode.id) {
          if (nodes.some(n => n.id === nodeIdInput)) {
              setIdError(true);
              // Revert on error or just show error? Revert is safer for now.
              // setNodeIdInput(selectedNode.id);
          } else {
              onRenameNode(selectedNode.id, nodeIdInput);
              setIdError(false);
          }
      } else {
          setIdError(false);
      }
  };

  if (isCollapsed) {
      return (
        <aside className="w-12 h-full bg-white border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col items-center py-4 shadow-[-1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
            <button 
              onClick={() => setIsCollapsed(false)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg transition-colors mb-4"
              title="Expand Properties"
            >
              <PanelRightOpen size={20} />
            </button>
            <div className="flex-1 flex items-center justify-center">
               <div className="-rotate-90 text-[10px] font-bold text-slate-400 whitespace-nowrap tracking-widest uppercase">
                  PROPERTIES
               </div>
            </div>
        </aside>
      );
  }

  // --- Edge Properties Mode ---
  if (selectedEdge) {
      const sourceNode = nodes.find(n => n.id === selectedEdge.source);
      const targetNode = nodes.find(n => n.id === selectedEdge.target);
      const isGateway = sourceNode?.type?.includes('Gateway');
      const availablePaths: string[] = sourceNode?.data?.outputs || [];
      
      const otherEdgesFromSource = edges.filter(e => e.source === selectedEdge.source && e.id !== selectedEdge.id);
      
      const handlePathChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newLabel = e.target.value;
          updateEdgeData(selectedEdge.id, { label: newLabel });
          setLocalData({ ...localData, label: newLabel });
      };

      const handleLabelChange = (val: string) => {
          updateEdgeData(selectedEdge.id, { label: val });
          setLocalData({ ...localData, label: val });
      };

      const isPathUsed = otherEdgesFromSource.some(e => e.label === localData.label);
      const isXor = sourceNode?.type === 'XorGateway';

      return (
        <aside className="w-[320px] h-full bg-white border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col shadow-[-1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 bg-slate-100 border border-slate-200 shadow-sm">
                            <ArrowRight size={16} />
                        </span>
                        <div>
                            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Connection</h2>
                            <p className="text-[10px] text-slate-400 font-mono">Key: {selectedEdge.id}</p>
                        </div>
                    </div>
                    <button 
                        onClick={() => setIsCollapsed(true)}
                        className="text-slate-400 hover:text-slate-600 p-1"
                    >
                        <PanelRightClose size={16} />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-800">
                        <span className="block text-[10px] text-slate-400 uppercase font-bold mb-1">From</span>
                        <div className="font-medium truncate" title={sourceNode?.data.label}>{sourceNode?.data.label || sourceNode?.id}</div>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-800">
                        <span className="block text-[10px] text-slate-400 uppercase font-bold mb-1">To</span>
                        <div className="font-medium truncate" title={targetNode?.data.label}>{targetNode?.data.label || targetNode?.id}</div>
                    </div>
                </div>

                {isGateway ? (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            <GitFork size={12} /> Select Path
                        </div>
                        
                        {availablePaths.length > 0 ? (
                            <select 
                                value={localData.label || ''} 
                                onChange={handlePathChange}
                                className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20"
                            >
                                <option value="">-- Unassigned --</option>
                                {availablePaths.map(path => (
                                    <option key={path} value={path}>{path}</option>
                                ))}
                            </select>
                        ) : (
                            <div className="text-xs text-amber-600 italic">
                                No paths defined on the gateway. Add paths in the gateway properties first.
                            </div>
                        )}

                        {isXor && isPathUsed && localData.label && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 items-start">
                                <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5"/>
                                <p className="text-xs text-amber-700">
                                    Warning: The path "<strong>{localData.label}</strong>" is already assigned to another outgoing connection. Exclusive gateways should have unique paths.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <PropertyField 
                        config={{ name: 'label', label: 'Label', type: 'text', placeholder: 'Connection name...' }}
                        value={localData.label || ''}
                        onChange={(k, v) => handleLabelChange(v)}
                    />
                )}
            </div>
        </aside>
      );
  }

  // --- Process Definition Mode ---
  if (!selectedNode) {
    if (!activeProcess) {
      return (
        <aside className="w-[320px] h-full bg-slate-50 border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex items-center justify-center relative">
          <button 
              onClick={() => setIsCollapsed(true)}
              className="absolute top-4 left-4 text-slate-400 hover:text-slate-600"
          >
              <PanelRightClose size={16} />
          </button>
          <div className="text-center text-slate-400">
             <p className="text-sm font-medium">No Selection</p>
          </div>
        </aside>
      );
    }

    const handleProcessChange = (key: string, value: any) => {
      setLocalData((prev: any) => ({ ...prev, [key]: value }));
      updateProcessData(activeProcess.id, { [key]: value });
    };

    return (
      <aside className="w-[320px] h-full bg-white border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col shadow-[-1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
           <div className="flex items-start justify-between">
               <div className="flex items-center gap-3">
                   <span className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 bg-slate-100 border border-slate-200 shadow-sm">
                       <Settings2 size={16} />
                   </span>
                   <div>
                       <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Process Settings</h2>
                       <p className="text-[10px] text-slate-400 font-mono">Definition Properties</p>
                   </div>
               </div>
               <button 
                  onClick={() => setIsCollapsed(true)}
                  className="text-slate-400 hover:text-slate-600 p-1"
               >
                  <PanelRightClose size={16} />
               </button>
           </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
             <PropertyField 
                config={{ name: 'name', label: 'Process Name', type: 'text', validation: { required: true } }}
                value={localData.name || ''}
                onChange={handleProcessChange}
             />
             <PropertyField 
                config={{ name: 'description', label: 'Description', type: 'textarea', rows: 4 }}
                value={localData.description || ''}
                onChange={handleProcessChange}
             />
             <PropertyField 
                config={{ name: 'id', label: 'Process Key', type: 'text', disabled: true }}
                value={localData.id || ''}
                onChange={() => {}}
             />
        </div>
      </aside>
    );
  }

  // --- Node Properties Mode ---
  const config = nodeTypes[selectedNode.type || ''] || nodeTypes['FormStartEvent'];
  const nodeConfig = propertyRegistry[selectedNode.type || ''] || { type: 'default', tabs: [{ id: 'properties', label: 'General', fields: [] }] };

  // @ts-ignore
  const ConfigIcon = (config.icon ? (LucideIcons[config.icon] || Settings2) : Settings2);

  const propertiesTab = nodeConfig.tabs.find(t => t.id === 'properties');
  const hasAdvancedConfig = nodeConfig.tabs.length > 1;

  const handleNodeChange = (key: string, value: any) => {
    // Use functional update to ensure we always have the latest state,
    // especially when multiple updates happen synchronously (e.g. from EmailTemplateTab)
    setLocalData((prev: any) => ({ ...prev, [key]: value }));
    
    if (selectedNode) {
        updateNodeData(selectedNode.id, { [key]: value });
    }
  };

  const toggleBreakpoint = () => {
    handleNodeChange('hasBreakpoint', !localData.hasBreakpoint);
  };

  const shouldRenderField = (field: any) => {
      if (!field.condition) return true;
      const { field: targetField, operator, value } = field.condition;
      const targetValue = localData[targetField];
      
      switch (operator) {
          case 'equals': return targetValue === value;
          case 'notEquals': return targetValue !== value;
          case 'contains': return Array.isArray(targetValue) && targetValue.includes(value);
          default: return true;
      }
  };

  const renderSimpleTabContent = (tab: TabConfig) => {
      // Use injected component if available
      if (tab.specialComponent && componentRegistry[tab.specialComponent]) {
          const Component = componentRegistry[tab.specialComponent];
          return <Component 
            data={localData} 
            onChange={handleNodeChange} 
            nodeType={selectedNode.type} 
            context={variableContext} 
          />;
      }

      return (
          <div className="space-y-4">
              {tab.fields.map(field => {
                  if (!shouldRenderField(field)) return null;
                  return (
                    <PropertyField
                        key={field.name}
                        config={field}
                        value={localData[field.name]}
                        onChange={handleNodeChange}
                    />
                  );
              })}
          </div>
      );
  };

  return (
    <aside className="w-[320px] h-full bg-white border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col shadow-[-1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 mb-1 min-w-0 flex-1">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm shrink-0" style={{ color: config.color }}>
                    <ConfigIcon size={16} />
                </span>
                <div className="min-w-0 flex-1 pr-2">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate pr-2">{config.label}</h2>
                    <input 
                        className={`text-[10px] font-mono bg-transparent border-b border-transparent focus:border-teal-500 rounded-sm px-0 w-full outline-none transition-all
                            ${idError ? 'text-red-500 border-red-500' : 'text-slate-400 hover:text-slate-600 focus:text-slate-600 dark:hover:text-slate-300 dark:focus:text-slate-300'}
                        `}
                        value={nodeIdInput}
                        onChange={(e) => {
                            setNodeIdInput(e.target.value);
                            setIdError(false);
                        }}
                        onBlur={handleIdBlur}
                        onKeyDown={(e) => {
                            if(e.key === 'Enter') {
                                e.currentTarget.blur();
                            }
                        }}
                        title={idError ? "Key already exists" : "Click to edit Key"}
                    />
                </div>
            </div>
            <div className="flex gap-1 shrink-0">
                <button 
                    onClick={toggleBreakpoint}
                    className={`p-1.5 rounded-lg transition-all ${localData.hasBreakpoint ? 'bg-red-50 border border-red-200 text-red-600' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                    title="Toggle Breakpoint"
                >
                    <Bug size={14} fill={localData.hasBreakpoint ? "currentColor" : "none"} />
                </button>
                <button 
                    onClick={() => setIsCollapsed(true)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                    title="Collapse"
                >
                    <PanelRightClose size={14} />
                </button>
            </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
         {hasAdvancedConfig && (
            <div className="mb-6">
                <button 
                    onClick={onOpenModal} 
                    className="w-full py-2 bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 text-white text-xs font-bold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 group"
                >
                    <Maximize2 size={12} className="text-slate-400 group-hover:text-white transition-colors"/> 
                    Open Advanced Editor
                </button>
            </div>
         )}
         
         <div className="space-y-4">
             <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Properties</h3>
             {propertiesTab ? renderSimpleTabContent(propertiesTab) : (
                 <p className="text-xs text-slate-400 italic">No basic properties available.</p>
             )}
         </div>
      </div>
      
      <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-center">
           <p className="text-[10px] text-slate-400">Double-click node to open advanced editor</p>
      </div>
    </aside>
  );
};

export default PropertiesPanel;
